import { useState } from "react";
import { MailQuestion, X } from "lucide-react";
import { Button } from "./ui/button";
import EmailComposer from "./EmailComposer";

// Job-linked resume-permission draft (task 2cdb320f, Goal 1 Slice 2).
// Opens EmailComposer pre-configured for the resume_permission template with
// BOTH the candidate and the specific job in context, so the AI-polished draft
// fills firm + role correctly ("<first> — <firm> has an opening for a <role>.
// Okay to submit your resume?"). Mounted from a strong match on both the job's
// Matches list (JobAlerts/Show) and the candidate's Matching Jobs tab.
//
// Note: the deterministic "Generate Draft" leaves {{firm_name}}/{{role}}
// literal (EmailRenderer only fills dotted {{obj.field}} vars, templates use
// bare ones) — the real copy comes from "Polish with AI", which reads
// job.firm_name/job.title from context. Steven polishes, reviews, sends.

interface PermissionCandidate {
  first_name: string;
  last_name?: string;
  email: string;
}

interface PermissionJob {
  firm_name: string;
  title: string;
  practice_area?: string;
}

interface Props {
  candidate: PermissionCandidate;
  job: PermissionJob;
  aiAssist: boolean;
  size?: "default" | "sm";
}

export default function AskPermissionButton({ candidate, job, aiAssist, size = "sm" }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button variant="outline" size={size} onClick={() => setOpen(true)}>
        <MailQuestion className="w-3.5 h-3.5 mr-1" /> Ask permission
      </Button>

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Ask permission to submit</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {candidate.first_name} → {job.firm_name}{job.title ? ` · ${job.title}` : ""}
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              <EmailComposer
                templateTypes={["resume_permission"]}
                defaultType="resume_permission"
                aiAssist={aiAssist}
                recipientEmail={candidate.email}
                context={{
                  candidate: {
                    first_name: candidate.first_name,
                    last_name:  candidate.last_name ?? "",
                    email:      candidate.email,
                  },
                  job: {
                    firm_name:     job.firm_name,
                    title:         job.title,
                    practice_area: job.practice_area ?? "",
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
